import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Eye, ArrowUpDown } from 'lucide-react';

export interface MobileAssignment {
  operatorId: number;
  operatorName?: string;
  cycleId: number;
  cycleName?: string;
  assignmentIds?: number[];
  campaignsCount?: number;
  storesCount?: number;
  assignmentsCount?: number;
  lastUpdatedAt?: string | null;
  lastSentAt?: string | null;
  sentMobileAt?: string | null;
}

export interface MobileAssignmentColumnHandlers {
  onPreview: (assignment: MobileAssignment) => void;
}

export function createMobileAssignmentColumns(
  handlers: MobileAssignmentColumnHandlers
): ColumnDef<MobileAssignment>[] {
  const { onPreview } = handlers;

  // Helper function to format date
  const formatDate = (assignment: MobileAssignment) => {
    const dateValue = assignment.lastUpdatedAt || assignment.lastSentAt;
    if (!dateValue) return '-';
    try {
      return new Date(dateValue).toLocaleString();
    } catch (e) {
      return '-';
    }
  };

  // Helper function to format assignment IDs
  const formatAssignmentIds = (assignmentIds?: number[]) => {
    if (!assignmentIds || assignmentIds.length === 0) {
      return null;
    }
    if (assignmentIds.length <= 5) {
      return assignmentIds.join(', ');
    }
    return `${assignmentIds.slice(0, 5).join(', ')}... (+${assignmentIds.length - 5} more)`;
  };

  return [
    // Operator column
    {
      accessorKey: 'operatorName',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Operator
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => row.original.operatorName || '-',
    },
    // Cycle column
    {
      accessorKey: 'cycleName',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Cycle
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => row.original.cycleName || '-',
    },
    // Assignment IDs column
    {
      id: 'assignmentIds',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Assignment ID
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const display = formatAssignmentIds(row.original.assignmentIds);
        return display ? (
          <span className="font-mono text-sm">{display}</span>
        ) : (
          <span className="text-gray-400">-</span>
        );
      },
    },
    // Campaigns column
    {
      accessorKey: 'campaignsCount',
      header: ({ column }) => {
        return (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              className="h-8 px-2"
            >
              Campaigns
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => (
        <div className="text-right">{row.original.campaignsCount ?? 0}</div>
      ),
    },
    // Stores column
    {
      accessorKey: 'storesCount',
      header: ({ column }) => {
        return (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              className="h-8 px-2"
            >
              Stores
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => (
        <div className="text-right">{row.original.storesCount ?? 0}</div>
      ),
    },
    // Total Tasks column
    {
      accessorKey: 'assignmentsCount',
      header: ({ column }) => {
        return (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              className="h-8 px-2"
            >
              Total Tasks
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => (
        <div className="text-right font-medium">{row.original.assignmentsCount ?? 0}</div>
      ),
    },
    // Last Sent column
    {
      id: 'lastSent',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Last Sent
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="text-gray-600">{formatDate(row.original)}</span>
      ),
    },
    // Actions column
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const assignment = row.original;
        return (
          <Button
            variant="outline"
            size="sm"
            className="text-blue-600 border-blue-300 hover:bg-blue-50"
            onClick={() => onPreview(assignment)}
            title="Preview campaigns, formats, and stores"
          >
            <Eye className="w-4 h-4 mr-1" />
            Preview
          </Button>
        );
      },
      enableSorting: false,
    },
  ];
}
